import { SubmitHandler, useForm } from 'react-hook-form';
import { Flex, Select, Spacer, VStack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { TaxResidency, UserProfile } from '__generated__/GQL';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import useCountriesList from 'common/countries/useCountriesList';
import zodLocales from 'common/zod/locales';
import Accordion from 'components/_core/Accordion';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import Loader from 'components/Loader';
import { FRANCE_COUNTRY_CODE } from 'features/Bento/libs/constants';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';
import { getCountryByCode } from 'helpers/address';

import { FRENCH_DROM_COUNTRY_CODES } from './constants';
import locales from './locales';

const TAX_IDENTIFICATION_NUMBER_REGEX = /^[\d\sA-Za-z]+$/;

const foreignTaxResidencySchema = z.object({
  country: z.string().length(2),
  fiscalId: z
    .string()
    .trim()
    .min(1, zodLocales.errors.required)
    .regex(
      TAX_IDENTIFICATION_NUMBER_REGEX,
      locales.taxIdentificationNumberInvalidFormat,
    ),
});

export type ForeignTaxResidencySchema = z.infer<
  typeof foreignTaxResidencySchema
>;

interface ForeignTaxResidencyProps {
  isSaving: boolean;
  onSubmit: SubmitHandler<ForeignTaxResidencySchema>;
  taxResidency: Pick<TaxResidency, 'country' | 'fiscalId'> | null;
  userProfile: Pick<UserProfile, 'isEligibleToOnboard'>;
}

/**
 * A form to retrieve the user's tax residency in two steps.
 * First the user selects their country of tax residence, we call onSubmit a first time to check the country's eligibility.
 * Then we ask for their tax identification number, and call onSubmit a final time to save it.
 * If the country was not eligible, the machine would've handled the navigation to a blocker screen at this point.
 */
const ForeignTaxResidency = asBentoPage<ForeignTaxResidencyProps>(
  ({ isSaving, Layout, onPrev, onSubmit, taxResidency, userProfile }) => {
    const isLastSavedCountryValid =
      taxResidency?.country && userProfile?.isEligibleToOnboard;

    const {
      formState: { errors, isValid },
      handleSubmit,
      register,
      watch,
    } = useForm<ForeignTaxResidencySchema>({
      defaultValues: {
        // Reset the country field if invalid so the user won't be blocked
        // on this page as the navigation happens only on field change
        country: isLastSavedCountryValid ? taxResidency?.country : '',
        fiscalId: taxResidency?.fiscalId,
      },
      mode: 'onChange',
      resolver: zodResolver(foreignTaxResidencySchema),
    });

    const countriesOptions = useCountriesList()
      .filter(
        ({ value }) =>
          ![...FRENCH_DROM_COUNTRY_CODES, FRANCE_COUNTRY_CODE].includes(value),
      )
      .map(({ emoji, label, value }) => (
        <option key={value} value={value}>
          {`${label} ${emoji}`}
        </option>
      ));

    const watchCountry = watch('country');
    const submit = handleSubmit((values) => {
      onSubmit(values);
    });

    const hasSelectedCountryBeenCheckedValid =
      watchCountry === taxResidency?.country &&
      userProfile?.isEligibleToOnboard;
    const showLoader = isSaving && !hasSelectedCountryBeenCheckedValid;

    const onCountryChange = (country: string) =>
      onSubmit({ country, fiscalId: '' });

    return (
      <Layout
        data-testid="foreign-tax-residency"
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height="full">
          <Heading marginBottom="space-16" size="lg">
            {locales.title}
          </Heading>

          <Text marginBottom="space-40">{locales.description}</Text>

          <VStack
            align="stretch"
            as="form"
            height="full"
            onSubmit={submit}
            spacing="space-24"
          >
            <Form.Field label={locales.countryLabel}>
              <Select
                {...register('country', {
                  onChange: (e) => onCountryChange(e.target.value),
                })}
                data-testid="tax-residency-country-input"
                placeholder={locales.countryPlaceholder}
              >
                {countriesOptions}
              </Select>
            </Form.Field>

            {showLoader ? <Loader /> : null}

            {hasSelectedCountryBeenCheckedValid ? (
              <>
                <Form.Field
                  error={errors.fiscalId?.message}
                  label={
                    locales.formatString(locales.taxIdentificationNumberLabel, {
                      taxResidencyCountry: getCountryByCode(
                        taxResidency?.country,
                      ),
                    }) as string
                  }
                  // @todo (migration turborepo) add back that prop if it really was displayed
                  // placeholder={locales.taxIdentificationNumberPlaceholder}
                >
                  <Form.Input
                    {...register('fiscalId')}
                    data-testid="tax-identification-number-input"
                  />
                </Form.Field>

                <Accordion>
                  <Accordion.Item title={locales.helpTitle}>
                    <Accordion.Text>{locales.helpDescription}</Accordion.Text>
                  </Accordion.Item>
                </Accordion>

                <Spacer />

                <OBStickyButton
                  data-testid="next"
                  isDisabled={!isValid}
                  isLoading={isSaving}
                  type="submit"
                >
                  {locales.nextButtonLabel}
                </OBStickyButton>
              </>
            ) : null}
          </VStack>
        </Flex>
      </Layout>
    );
  },
);

export default ForeignTaxResidency;
